import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { SharedLayout } from '../components/layout/SharedLayout';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';

const PageWrapper = styled.div`
  min-height: 80vh;
  background: linear-gradient(135deg, #f8fafb 0%, #e8f4f8 100%);
  margin-top: -80px;
  padding-top: 80px;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: ${theme.spacing['2xl']} ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.xl} ${theme.spacing.md};
  }
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: ${theme.spacing['4xl']};
`;

const PageTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  color: #232B3B;
  margin-bottom: ${theme.spacing.lg};
  line-height: 1.1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #666;
  max-width: 700px;
  margin: 0 auto ${theme.spacing['2xl']};
  line-height: 1.6;
`;

const TrustIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing['2xl']};
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: ${theme.spacing.lg};
  }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  color: #555;
  font-weight: 600;
  
  &::before {
    content: '✓';
    color: #28a745;
    font-weight: bold;
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const CTASection = styled.section`
  background: white;
  border-radius: 16px;
  padding: ${theme.spacing['2xl']};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  margin-bottom: ${theme.spacing['3xl']};
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #232B3B;
  margin-bottom: ${theme.spacing.lg};
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: ${theme.spacing['2xl']};
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.xl};
`;

const PrimaryButton = styled.button`
  background: #ffd700;
  color: #232B3B;
  border: none;
  padding: 16px 32px;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
  
  &:hover {
    background: #ffed4a;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
  }
`;

const SecondaryButton = styled.a`
  background: white;
  color: #232B3B;
  border: 2px solid #232B3B;
  padding: 14px 30px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-block;
  
  &:hover {
    background: #232B3B;
    color: white;
    transform: translateY(-2px);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.xl};
  align-items: center;
  flex-wrap: wrap;
  font-size: 1.1rem;
  color: #666;
`;

const PhoneLink = styled.a`
  color: #232B3B;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover {
    color: #ffd700;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing['3xl']};
`;

const BenefitCard = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
  }
`;

const BenefitIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${theme.spacing.lg};
`;

const BenefitTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #232B3B;
  margin-bottom: ${theme.spacing.md};
`;

const BenefitDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const TestimonialSection = styled.section`
  background: white;
  border-radius: 16px;
  padding: ${theme.spacing['2xl']};
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: ${theme.spacing['3xl']};
`;

const Blockquote = styled.blockquote`
  font-size: 1.2rem;
  font-style: italic;
  color: #444;
  margin-bottom: ${theme.spacing.lg};
  line-height: 1.6;
  
  &::before, &::after {
    content: '"';
    color: #ffd700;
    font-size: 2rem;
    font-weight: bold;
  }
`;

const TestimonialSource = styled.cite`
  color: #666;
  font-weight: 600;
`;

export const Retirevana: React.FC = () => {
  const { openCalendly } = useCalendlyPopup();
  
  return (
    <SharedLayout>
      <PageWrapper>
        <Container>
          <HeroSection>
            <PageTitle>Schedule Your Free Consultation</PageTitle>
            <Subtitle>
              Get personalized annuity guidance from experts with 60+ years of family experience. 
              No cost, no obligation, no pressure.
            </Subtitle>
            
            <TrustIndicators>
              <TrustItem>10,000+ Clients Served</TrustItem>
              <TrustItem>Zero Complaints</TrustItem>
              <TrustItem>BBB A+ Rating</TrustItem>
              <TrustItem>60+ Years Experience</TrustItem>
            </TrustIndicators>
          </HeroSection>

          <CTASection>
            <CTATitle>Ready to Find Your Perfect Annuity?</CTATitle>
            <CTADescription>
              Book a 15-minute consultation with one of our experienced advisors. We'll help you understand 
              your options and find the annuity that best fits your retirement goals.
            </CTADescription>
            
            <ButtonGroup>
              <PrimaryButton onClick={openCalendly}>
                Schedule Free Consultation
              </PrimaryButton>
              <SecondaryButton href="tel:800-922-1894">
                Call Now: 800-922-1894
              </SecondaryButton>
            </ButtonGroup>
            
            <ContactInfo>
            </ContactInfo>
          </CTASection>

          <BenefitsGrid>
            <BenefitCard>
              <BenefitIcon></BenefitIcon>
              <BenefitTitle>100% Free Service</BenefitTitle>
              <BenefitDescription>
                No cost to you ever. We're compensated by the annuity companies, 
                so our advice comes at no charge to you.
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon>👥</BenefitIcon>
              <BenefitTitle>Three Generations</BenefitTitle>
              <BenefitDescription>
                The Averill family has been helping retirees for over 60 years. 
                Trust the experience that spans three generations.
              </BenefitDescription>
            </BenefitCard>
          </BenefitsGrid>

          <TestimonialSection>
            <Blockquote>
              We are not here to tell you what to do with your money. We simply share our 
              methodology to help you make the best decision for your retirement.
            </Blockquote>
            <TestimonialSource>— Cap Averill II & Associates Philosophy</TestimonialSource>
          </TestimonialSection>

          <CTASection>
            <CTATitle>Take the Next Step</CTATitle>
            <CTADescription>
              Ready to secure your retirement? Schedule your consultation today or learn more about annuities first.
            </CTADescription>
            
            <ButtonGroup>
              <PrimaryButton onClick={openCalendly}>
                Book My Consultation
              </PrimaryButton>
              <SecondaryButton href="/annuity-basics">
                Learn Annuity Basics
              </SecondaryButton>
            </ButtonGroup>
          </CTASection>
        </Container>
      </PageWrapper>
    </SharedLayout>
  );
};